import type { ReactNode } from 'react';
import { cn } from './react';
import type { FormControlProps } from './types';

export type CheckboxOption<V> = {
  key: string | number;
  value: V;
  disabled?: boolean;
  hidden?: boolean;
  label?: ReactNode;
  className?: string;
};

export type CheckboxProps<M extends boolean, V, O = CheckboxOption<V>> = Omit<
  FormControlProps<M extends true ? V[] : V>,
  'required'
> & {
  multiple?: M;
  indicator: 'before' | 'after';
  options: O[];
  className?: string;
};

export const Checkbox = <M extends boolean = false, V = unknown>({
  className,
  value,
  options,
  indicator,
  multiple,
  onChange,
  autoFocus,
  disabled,
  onBlur,
  onFocus,
}: CheckboxProps<M, V>) => (
  <div className={cn(className)}>
    {options
      .filter((option) => !option.hidden)
      .map((option, index) => {
        const isChecked = multiple
          ? (value as V[] | undefined)?.includes(option.value)
          : value === option.value;
        return (
          <button
            key={option.key}
            role={multiple ? 'checkbox' : 'radio'}
            className={cn(
              'flex items-center gap-2 group',
              'enabled:cursor-pointer',
              '!outline-0',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              indicator === 'after' && 'justify-between flex-row-reverse',
              option.className,
            )}
            type="button"
            disabled={option.disabled || disabled}
            autoFocus={autoFocus && index === 0}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={() => {
              if (multiple) {
                const valueList = (value ?? []) as V[];
                onChange?.(
                  (valueList.includes(option.value)
                    ? valueList.filter((x) => x !== option.value)
                    : [...valueList, option.value]) as never,
                );
              } else {
                onChange?.(option.value as never);
              }
            }}
            {...(isChecked && {
              'aria-checked': true,
            })}
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              className={cn(
                'border border-border-tertiary size-5 appearance-none pointer-events-none',
                'checked:before:size-full checked:border-content-brand transition-all before:transition-all',
                'outline outline-offset-0 outline-transparent group-hover:outline-border-tertiary group-focus:outline-content-brand',
                multiple
                  ? 'rounded-sm checked:before:bg-content-brand font-bold checked:before:content-["✓"] checked:before:text-content-primary-inverse checked:before:flex checked:before:items-center checked:before:justify-center'
                  : 'rounded-full checked:before:content-[""] checked:before:scale-75 checked:before:block checked:before:rounded-full checked:before:bg-brand-500 checked:before:mx-auto checked:before:my-auto',
              )}
              readOnly
              checked={isChecked}
              tabIndex={-1}
            />

            <div className="grow text-start">{option.label}</div>
          </button>
        );
      })}
  </div>
);
